import {
    includes as _includes,
    isEmpty as _isEmpty,
    isString as _isString,
    toLower as _toLower,
    toString as _toString,
    trim as _trim,
} from 'lodash';
import crc32 from 'crc/crc32';
import { environment } from 'src/environments/environment';

export class PrintSheetUtils {
    static constructItemDescription(
        sku: string,
        description: string,
        quantity: number,
        variation?: string,
        variationSku?: string,
    ) {
        let descString = `[${variationSku || sku}] ${description}`;
        if (quantity > 1) {
            descString = `(${quantity}) ${descString}`;
        }
        if (variation) {
            descString = `${descString} - ${variation}`;
        }
        return descString;
    }

    // Digits and consonants only: without vowels the code cannot spell a
    // real (possibly rude) word in English or transliterated Thai.
    private static readonly matchingAlphabet =
        '0123456789BCDFGHJKLMNPQRSTVWXYZ';

    static generateMatchingNumber(orderSn: string) {
        if (_isString(orderSn) && !_isEmpty(orderSn)) {
            const alphabet = PrintSheetUtils.matchingAlphabet;
            // CRC-32 folded into alphabet^4 so the code spreads across all
            // characters in all four positions.
            let code = crc32(orderSn) % Math.pow(alphabet.length, 4);
            let matching = '';
            for (let i = 0; i < 4; i++) {
                matching = alphabet[code % alphabet.length] + matching;
                code = Math.floor(code / alphabet.length);
            }
            return matching;
        }
        return '';
    }

    // Composes structured Thai address parts into the single line the printed
    // documents expect, using the Bangkok-aware prefixes (แขวง/เขต in Bangkok,
    // ตำบล/อำเภอ elsewhere). Country is omitted — these are domestic documents.
    // Missing parts are skipped, so a source that already stores the whole
    // address in line1 comes through unchanged.
    static constructThaiFullAddress(parts: {
        line1?: string | null;
        subDistrict?: string | null;
        district?: string | null;
        province?: string | null;
        postalCode?: string | null;
    }): string {
        const line1 = _trim(_toString(parts.line1));
        const subDistrict = _trim(_toString(parts.subDistrict));
        const district = _trim(_toString(parts.district));
        const province = _trim(_toString(parts.province));
        const postalCode = _trim(_toString(parts.postalCode));
        const isBangkok = _includes(province, 'กรุงเทพ');
        let result = line1;
        if (subDistrict) {
            result += `${isBangkok ? ' แขวง' : ' ตำบล'}${subDistrict}`;
        }
        if (district) {
            result += `${isBangkok ? ' เขต' : ' อำเภอ'}${district}`;
        }
        if (province) {
            result += ` จังหวัด${province}`;
        }
        if (postalCode) {
            result += ` ${postalCode}`;
        }
        return _trim(result);
    }

    static getShippingCarrierLogo(shippingCarrier: string): string {
        const carrier = _toLower(shippingCarrier);
        if (_includes(carrier, 'kerry')) {
            return environment.kerryLogoUrl;
        } else if (_includes(carrier, 'ninja van')) {
            return environment.ninjaLogoUrl;
        } else if (_includes(carrier, 'spx express')) {
            return environment.spxLogoUrl;
        } else if (_includes(carrier, 'shopee xpress')) {
            return environment.shopeeExpressLogoUrl;
        } else if (_includes(carrier, 'j&t express')) {
            return environment.jtLogoUrl;
        } else if (_includes(carrier, 'flash express')) {
            return environment.flashLogoUrl;
        }
        return '';
    }

    static base64ToBlob(base64: string, type = 'application/octet-stream') {
        const binStr = atob(base64);
        const len = binStr.length;
        const arr = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            arr[i] = binStr.charCodeAt(i);
        }
        return new Blob([arr], { type });
    }

    static blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => {
                const dataUrl = reader.result as string;
                resolve(dataUrl.substring(dataUrl.indexOf(',') + 1));
            };
            reader.readAsDataURL(blob);
        });
    }
}
